import { FC, Suspense, useMemo, useState } from 'react';
import { FiClock } from 'react-icons/fi';
import { useParams } from 'react-router';
import { TSTitleCard } from '../../../../components/shared/card/Card';
import { TableContainer } from '../../../../components/shared/table/TableContainer';
import { TableLoader } from '../../../../components/shared/table/TableLoader';
import { ReadEmployee } from '../../../../services/gql/graphql.generated';
import { useLocationService } from '../../../../services/location/LocationService';
import { EmployeeShiftsTable } from '../../../shifts/EmployeeShiftsTable';
import strings from '../../team.translations';
import { TeamMemberUpdateType, useTeamMemberService } from '../TeamMemberService';
import { MonthPicker } from '@/components/shared/date/MonthPicker';

interface ShiftsSectionProps {
  isMe?: boolean | undefined;
  userData: ReadEmployee;
}

export const shiftsSectionId = 'shifts-section';

export const ShiftsSection: FC<ShiftsSectionProps> = ({ userData, isMe }) => {
  const shiftsTableUpdateNeeded = useTeamMemberService(state => state.updateNeeded === TeamMemberUpdateType.SHIFTS);
  const updateComplete = useTeamMemberService(state => state.updateComplete);
  const forceUpdate = useTeamMemberService(state => state.forceUpdate);

  const [date, setDate] = useState<Date>(() => {
    const startDate = new Date();
    startDate.setUTCDate(1);
    startDate.setUTCHours(0, 0, 0);

    return startDate;
  });

  const endDate = useMemo(() => {
    const endDate = new Date(date);
    endDate.setUTCDate(1);
    endDate.setUTCMonth(endDate.getUTCMonth() + 1);
    endDate.setUTCDate(0);

    return endDate;
  }, [date]);

  const selectedShiftLocations = useLocationService(state => state.selectedShiftLocations);
  const { recordId } = useParams<'recordId'>();

  return (
    <section id={shiftsSectionId}>
      <TSTitleCard title={strings.verticalTabMenu.shiftsLabel} icon={FiClock}>
        <TableContainer>
          <div className="md:px-6 px-4 py-3 border-b">
            <MonthPicker value={date} onChange={setDate} showLabels={false} position="left" />
          </div>
          <Suspense fallback={<TableLoader />}>
            <EmployeeShiftsTable
              startDate={date}
              endDate={endDate}
              userId={userData.identityId}
              refetchComplete={() => updateComplete(TeamMemberUpdateType.SHIFTS)}
              needsRefetch={shiftsTableUpdateNeeded}
              onShiftEdit={() => forceUpdate(TeamMemberUpdateType.HEADER)}
              isMe={isMe}
              recordId={recordId}
              locations={selectedShiftLocations.filter(location => location.checked).map(location => location.key)}
            />
          </Suspense>
        </TableContainer>
      </TSTitleCard>
    </section>
  );
};
